/**
 * Selection utility functions for handling text selection in the debate views
 */

export interface SelectionIds {
  paragraphId: string | null;
  sentenceId: number | null;
  nodeKey: string | null;
}

export interface NodePosition {
  nodeKey: string;
  position: number;
}

export interface SelectionBounds {
  from: {
    nodeId: string;
    nodeKey: string;
    nodeOrder: number;
    textPosition: number;
    outlineGroundedNode: string;
  };
  to: {
    nodeId: string;
    nodeKey: string;
    nodeOrder: number;
    textPosition: number;
    outlineGroundedNode: string;
  };
}

export interface Target {
  type: 'annotation' | 'outlineNode' | 'article';
  ref: any;
  index?: number;
}

export interface Span {
  nodes: Array<{
    nodeKey: string;
    nodeContent: string;
    source: string;
  }>;
  fullText: string;
}

/**
 * Extract IDs from HTML element
 */
export function getIdsFromElement(element: HTMLElement): SelectionIds {
  const nodeRegex = /_N_(.+)/;
  const nodeMatch = nodeRegex.exec(element.id);
  const spanRegex = /_P_(.+)_S_(.+)/;
  const spanMatch = spanRegex.exec(element.id);

  return {
    paragraphId: spanMatch?.[1] || null,
    sentenceId: spanMatch ? parseInt(spanMatch[2]) : null,
    nodeKey: nodeMatch?.[1] || null,
  };
}

/**
 * Get previous sibling element
 */
export function getElementPreviousSibling(element: HTMLElement): HTMLElement | null {
  const parentElement = element.parentElement;
  if (!parentElement) return null;

  const parentChildren = Array.from(parentElement.children);
  const currentElementIdx = parentChildren.findIndex(child => child === element);
  const previousSibling =
    currentElementIdx > 0 ? (parentChildren[currentElementIdx - 1] as HTMLElement) : null;
  return previousSibling;
}

/**
 * Get next sibling element
 */
export function getElementNextSibling(element: HTMLElement): HTMLElement | null {
  const parentElement = element.parentElement;
  if (!parentElement) return null;

  const parentChildren = Array.from(parentElement.children);
  const currentElementIdx = parentChildren.findIndex(child => child === element);
  const nextSibling =
    currentElementIdx < parentChildren.length - 1
      ? (parentChildren[currentElementIdx + 1] as HTMLElement)
      : null;
  return nextSibling;
}

/**
 * Adjust start node selection from element
 */
export function adjustStartNodeSelectionFromElement(element: HTMLElement): NodePosition {
  const nextSibling = getElementNextSibling(element);
  // node contains empty space string
  if (nextSibling && !nextSibling.textContent?.trim().length) {
    return adjustStartNodeSelectionFromElement(nextSibling);
  } else {
    const nodeKey = getIdsFromElement(nextSibling!).nodeKey;
    return { nodeKey: nodeKey!, position: 0 };
  }
}

/**
 * Adjust end node selection from element
 */
export function adjustEndNodeSelectionFromElement(element: HTMLElement): NodePosition {
  const previousSibling = getElementPreviousSibling(element);
  // node contains empty space string
  if (previousSibling && !previousSibling.textContent?.trim().length) {
    return adjustEndNodeSelectionFromElement(previousSibling);
  } else {
    const nodeKey = getIdsFromElement(previousSibling!).nodeKey;
    return { nodeKey: nodeKey!, position: previousSibling!.textContent?.length || 0 };
  }
}

/**
 * Adjust start node selection from span
 */
export function adjustStartNodeSelectionFromSpan(node: any, span: Span): NodePosition {
  const nextSiblingIndex = span.nodes.findIndex(n => n.nodeKey === node.nodeKey) + 1;
  if (nextSiblingIndex <= 0) {
    return { nodeKey: node.nodeKey, position: node?.nodeContent?.length || 0 };
  }
  const nextSibling = span.nodes[nextSiblingIndex];

  // If no next sibling exists (we're at the end), use current node
  if (!nextSibling) {
    return { nodeKey: node.nodeKey, position: node?.nodeContent?.length || 0 };
  }

  // node contains empty space string
  if (nextSibling && !nextSibling.nodeContent.trim().length) {
    return adjustStartNodeSelectionFromSpan(nextSibling, span);
  } else {
    const nodeKey = nextSibling?.nodeKey;
    return { nodeKey: nodeKey!, position: 0 };
  }
}

/**
 * Adjust end node selection from span
 */
export function adjustEndNodeSelectionFromSpan(node: any, span: Span): NodePosition {
  const previousSiblingIndex = span.nodes.findIndex(n => n.nodeKey === node.nodeKey) - 1;
  if (previousSiblingIndex < 0) {
    return { nodeKey: node.nodeKey, position: node?.nodeContent?.length || 0 };
  }
  const previousSibling = span.nodes[previousSiblingIndex];

  // If no previous sibling exists (we're at the start), use current node
  if (!previousSibling) {
    return { nodeKey: node.nodeKey, position: node?.nodeContent?.length || 0 };
  }

  // node contains empty space string
  if (previousSibling && !previousSibling.nodeContent.trim().length) {
    return adjustEndNodeSelectionFromSpan(previousSibling, span);
  } else {
    const nodeKey = previousSibling?.nodeKey;
    return { nodeKey: nodeKey!, position: previousSibling?.nodeContent?.length || 0 };
  }
}

/**
 * Get node and position key from span
 */
export function nodeAndPositionKeyFromSpan(
  span: Span,
  offset: number,
  isSelectionStart: boolean
): NodePosition {
  const nodeKeyStartRegex =
    span.nodes[0].source === 'groundedTextNodes' ? /(?<=|G-[0-9]+-|T)[0-9]+/ : /(?<=T)[0-9]+/;
  const nodeKeyEndRegex =
    span.nodes[0].source === 'groundedTextNodes'
      ? /(?<=G-[0-9]+-|T|[0-9]+-)[0-9]+/
      : /(?<=T[0-9]+-)[0-9]+/;

  const firstNodeKey = parseInt(span.nodes[0].nodeKey.match(nodeKeyStartRegex)![0]);
  const inSpanOffset = firstNodeKey + offset;
  let nodeIdx = span.nodes.findIndex(
    node => parseInt(node.nodeKey.match(nodeKeyEndRegex)![0]) >= inSpanOffset
  );
  if (nodeIdx < 0) nodeIdx = span.nodes.length - 1;
  else if (nodeIdx >= span.nodes.length) nodeIdx = span.nodes.length - 1;

  if (span.nodes[0].source === 'groundedTextNodes') {
    nodeIdx = nodeIdx - 1;
  }
  const node = span.nodes[nodeIdx];

  if (isSelectionStart) {
    const nodeKeyEnd = parseInt(node.nodeKey.match(nodeKeyEndRegex)![0]);
    // selecting an empty space
    if (!node.nodeContent.trim().length || nodeKeyEnd <= inSpanOffset) {
      const adjustedNode = adjustStartNodeSelectionFromSpan(node, span);
      return adjustedNode;
    }
  } else {
    const nodeKeyStart = parseInt(node.nodeKey.match(nodeKeyStartRegex)![0]);
    // selecting an empty space
    if (!node.nodeContent.trim().length || nodeKeyStart >= inSpanOffset) {
      const adjustedNode = adjustEndNodeSelectionFromSpan(node, span);
      return adjustedNode;
    }
  }
  return {
    nodeKey: node.nodeKey,
    position: node.nodeContent.length,
  };
}

/**
 * Get selection direction from Selection object
 */
export function getSelectionDirection(selection: Selection): number {
  //
  // get selection elements
  const anchorElement = (selection.anchorNode as any)?.id
    ? selection.anchorNode
    : selection.anchorNode?.parentElement;
  const focusElement = (selection.focusNode as any)?.id
    ? selection.focusNode
    : selection.focusNode?.parentElement;

  if (
    !anchorElement ||
    !focusElement ||
    !(anchorElement as HTMLElement).id ||
    !(focusElement as HTMLElement).id
  ) {
    return 1; // default to forward direction
  }

  //
  // find position from node or span id
  const spanRegex = /_P_(.+)_S_(.+)/;
  const anchorElementSpanMatch = spanRegex.exec((anchorElement as HTMLElement).id);
  const focusElementSpanMatch = spanRegex.exec((focusElement as HTMLElement).id);

  const nodeRegex = /_P_(.+)_S_(.+)_N_T(.+)-.+/;
  const anchorElementNodeMatch = nodeRegex.exec((anchorElement as HTMLElement).id);
  const focusElementNodeMatch = nodeRegex.exec((focusElement as HTMLElement).id);

  //
  // extract paragraph, sentence, text node, and offset data
  const anchor = {
    pId: anchorElementSpanMatch ? parseInt(anchorElementSpanMatch[1]) : 0,
    sId: anchorElementSpanMatch ? parseInt(anchorElementSpanMatch[2]) : 0,
    nId: anchorElementNodeMatch && parseInt(anchorElementNodeMatch[3]),
    offset: selection.anchorOffset,
  };

  const focus = {
    pId: focusElementSpanMatch ? parseInt(focusElementSpanMatch[1]) : 0,
    sId: focusElementSpanMatch ? parseInt(focusElementSpanMatch[2]) : 0,
    nId: focusElementNodeMatch && parseInt(focusElementNodeMatch[3]),
    offset: selection.focusOffset,
  };

  //
  // compare
  if (
    anchor.pId < focus.pId || // paragraph compare
    (anchor.pId === focus.pId &&
      (anchor.sId < focus.sId || // sentence compare
        (anchor.sId === focus.sId &&
          ((anchor.nId &&
            focus.nId && // text nodes compare
            (anchor.nId < focus.nId ||
              (anchor.nId === focus.nId && anchor.offset <= focus.offset))) || // offsets compare
            (!(anchor.nId && focus.nId) && anchor.offset <= focus.offset))))) // offsets compare
  ) {
    return 1; // forward direction
  } else {
    return -1; // backward direction
  }
}

/**
 * Get node key and position from HTML element
 */
export function getNodeKeyAndPositionFromElement(
  element: HTMLElement,
  offset: number,
  edge: 'anchor' | 'focus',
  target: Target,
  direction: number
): NodePosition {
  const isSelectionStart =
    (edge === 'anchor' && direction > 0) || (edge === 'focus' && direction < 0);

  // find position from node id
  const nodeKey = getIdsFromElement(element).nodeKey;
  if (nodeKey) {
    if (isSelectionStart) {
      // selecting an empty space
      if (!element.textContent?.trim().length || offset >= element.textContent.length) {
        const adjustedNode = adjustStartNodeSelectionFromElement(element);
        return adjustedNode;
      }
    } else {
      // selecting an empty space
      if (!element.textContent?.trim().length || offset === 0) {
        const adjustedNode = adjustEndNodeSelectionFromElement(element);
        return adjustedNode;
      }
    }
    return { nodeKey, position: offset };
  }

  // find position from span id
  const spanMatch = getIdsFromElement(element);
  if (spanMatch.paragraphId) {
    const paragraphId = spanMatch.paragraphId;
    const spanIdx = spanMatch.sentenceId!;
    let span: Span | null = null;

    // get corresponding span element by target type
    // outline
    if (target?.type === 'outlineNode') {
      span = target.ref.$refs.outlineInput.outlineParagraphs[paragraphId].spans[spanIdx];
      const isAtEndOfSpan = offset >= span.fullText.trim().length;
      if (isAtEndOfSpan) {
        if (isSelectionStart) {
          const nextSibling = getElementNextSibling(element);
          if (nextSibling) {
            const siblingSpan = getIdsFromElement(nextSibling);
            span =
              target.ref.$refs.outlineInput.outlineParagraphs[siblingSpan.paragraphId!].spans[
                siblingSpan.sentenceId!
              ];
            offset = 0;
          }
        }
      }

      // annotation
    } else if (target?.type === 'annotation') {
      span = target.ref.annotationParagraphs.find((p: any) => p.paragraphId === paragraphId).spans[
        spanIdx
      ];

      // article
    } else {
      span = target.ref.articleParagraphs.find((p: any) => p.paragraphId === paragraphId).spans[
        spanIdx
      ];
      //
      // adjust span when selection falls at the end of a span
      const isAtEndOfSpan = offset >= span.fullText.trim().length;
      if (isAtEndOfSpan) {
        if (isSelectionStart) {
          const nextSibling = getElementNextSibling(element);
          if (nextSibling) {
            const siblingSpanIdx = getIdsFromElement(nextSibling).sentenceId!;
            span = target.ref.articleParagraphs.find((p: any) => p.paragraphId === paragraphId)
              .spans[siblingSpanIdx];
            offset = 0;
          }
        }
      }
    }

    // validate span exists
    if (span) {
      return nodeAndPositionKeyFromSpan(span, offset, isSelectionStart);
    }
  }

  // return non-existing negative nodeKey and position
  return { nodeKey: '-1', position: -1 };
}

/**
 * Create annotation node info object
 */
export function getAnnotationNodeInfo(
  id: string,
  nodeKey: string,
  nodeOrder: number,
  position: number,
  outlineGroundedNode: string
) {
  return {
    nodeId: id,
    nodeKey: nodeKey,
    nodeOrder: nodeOrder,
    textPosition: position,
    outlineGroundedNode: outlineGroundedNode,
  };
}

/**
 * Main orchestrator function that computes selection bounds
 */
export function computeSelectionBounds(
  event: Event,
  target: Target,
  direction: number,
  nodes: any,
  selection: Selection,
  anchorElement: HTMLElement,
  focusElement: HTMLElement,
  customOffset: number
): SelectionBounds {
  // calculate anchor node position
  const { nodeKey: anchorNodeKeyBase, position: anchorNodePositionBase } =
    getNodeKeyAndPositionFromElement(
      anchorElement,
      selection.anchorOffset,
      'anchor',
      target,
      direction
    );

  // calculate focus node position
  const { nodeKey: focusNodeKeyBase, position: focusNodePositionBase } =
    getNodeKeyAndPositionFromElement(focusElement, customOffset, 'focus', target, direction);

  // calculate first and last node (depending on user highlighting from left-to-right or right-to-left)
  const anchorNodeOrder = nodes[anchorNodeKeyBase];
  const focusNodeOrder = nodes[focusNodeKeyBase];

  let nodePositions: { anchor: number; focus: number };
  let nodeKeys: { anchor: string; focus: string };
  let elements: { anchor: HTMLElement; focus: HTMLElement };

  if (anchorElement.id < focusElement.id) {
    nodePositions = { anchor: anchorNodePositionBase, focus: focusNodePositionBase };
    nodeKeys = { anchor: anchorNodeKeyBase, focus: focusNodeKeyBase };
    elements = { anchor: anchorElement, focus: focusElement };
  } else if (anchorElement.id > focusElement.id) {
    nodePositions = { anchor: focusNodePositionBase, focus: anchorNodePositionBase };
    nodeKeys = { anchor: focusNodeKeyBase, focus: anchorNodeKeyBase };
    elements = { anchor: focusElement, focus: anchorElement };
  } else if (
    parseInt(anchorNodeOrder.nodeKey.split('T')[1]) <=
    parseInt(focusNodeOrder.nodeKey.split('T')[1])
  ) {
    nodePositions = { anchor: anchorNodePositionBase, focus: focusNodePositionBase };
    nodeKeys = { anchor: anchorNodeKeyBase, focus: focusNodeKeyBase };
    elements = { anchor: anchorElement, focus: focusElement };
  } else {
    nodePositions = { anchor: focusNodePositionBase, focus: anchorNodePositionBase };
    nodeKeys = { anchor: focusNodeKeyBase, focus: anchorNodeKeyBase };
    elements = { anchor: focusElement, focus: anchorElement };
  }

  let outlineGroundedNode = '';
  if (
    target.type === 'outlineNode' &&
    target.index !== -1 &&
    target.ref.outlineSource.outlineType !== 'Human-Outline'
  ) {
    outlineGroundedNode = target.index!.toString();
  }

  return {
    from: getAnnotationNodeInfo(
      elements.anchor.id,
      nodeKeys.anchor,
      nodes[nodeKeys.anchor].nodeOrder,
      nodePositions.anchor,
      outlineGroundedNode
    ),
    to: getAnnotationNodeInfo(
      elements.focus.id,
      nodeKeys.focus,
      nodes[nodeKeys.focus].nodeOrder,
      nodePositions.focus,
      outlineGroundedNode
    ),
  };
}
